import React, {
  useMemo,
  useLayoutEffect,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { DateTime } from 'luxon';

import { Popup } from '../PopupMenu/Popup';
import { Input } from '../Input/Input';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { S } from '../Typography/Typography';

import {
  formatTime,
  getTimesFromUnit,
  mapTimesToTimeOptions,
  isOptionSelectedTime,
} from './helpers';
import type { Option, TimeOption } from './helpers';
import { TimeIntervals } from './TimePicker';
import { useScrollToSelectedChild } from '../../hooks/useScrollToSelectedChild';

import styles from './DatePickers.scss';
import timePickerStyles from './TimePicker.scss';
import timePickerButtonStyles from './TimePickerButton.scss';

export type TimePickerButtonProps = {
  disabled?: boolean;
  value?: DateTime | null;
  timeZone?: string;
  onChange?: (value: string) => void;
  unit?: TimeIntervals;
  times?: TimeOption[];
  className?: string;
  wrapperClassName?: string;
  buttonClassName?: string;
  hideLabel?: boolean;
  label?: string;
  error?: string[];
  fixedLabel?: boolean;
  showTimezone?: boolean;
  testId?: string;
  placeholder?: string;
  locale?: string;
  ButtonComponent?: ({
    value,
    onOpen,
    ...props
  }: {
    value: string;
    onOpen: (e: React.MouseEvent<HTMLElement>) => void;
  } & Record<string, unknown>) => React.ReactElement;
};

type DefaultButtonProps = {
  buttonText: string;
  buttonClassName?: string;
  disabled?: boolean;
  testId?: string;
  hideLabel?: boolean;
  label?: string;
  placeholder?: string;
  error?: string[];
  fixedLabel?: boolean;
  styles: Record<string, string>;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const DefaultButton = React.forwardRef<HTMLButtonElement, DefaultButtonProps>(
  (
    {
      buttonText,
      buttonClassName = '',
      disabled = false,
      testId = 'timePickerButton',
      hideLabel = false,
      label,
      placeholder,
      error,
      fixedLabel = false,
      styles,
      ...buttonProps
    },
    ref
  ) => {
    const { t } = useTranslation();

    const labelText =
      label || t('web.admin.content.sections.filters.byHourlyTime.label');
    const placeholderText =
      placeholder ||
      t('web.admin.content.sections.filters.byHourlyTime.placeholder');

    return (
      <button
        ref={ref}
        className={cx(buttonClassName, styles.button, {
          [styles.disabled]: disabled,
        })}
        data-test={testId}
        disabled={disabled}
        type="button"
        {...buttonProps}
      >
        <Input
          className={styles.input}
          icon="clock"
          iconRight
          value={buttonText}
          placeholder={placeholderText}
          label={hideLabel ? '' : labelText}
          ariaLabel={labelText}
          onChange={() => null}
          boldBorder
          showClear={false}
          error={error}
          fixedLabel={fixedLabel}
          readOnly
        />
      </button>
    );
  }
);

DefaultButton.displayName = 'DefaultButton';

type RenderOptionsProps = {
  close: () => void;
  options: Option[];
  selectedTime: string;
  handleSelectOption: (option: Option, close: () => void) => void;
  optionsRef: React.RefObject<HTMLDivElement | null>;
};

const renderOptions = ({
  close,
  options,
  selectedTime,
  handleSelectOption,
  optionsRef,
}: RenderOptionsProps) => (
  <div
    ref={optionsRef}
    className={cx(
      timePickerStyles.optionsContainer,
      timePickerButtonStyles.TimePickerButtonOptionsContainer
    )}
  >
    {options.map(option => (
      <div
        key={option.label}
        className={cx(timePickerButtonStyles.TimePickerButtonOption, {
          [timePickerButtonStyles.TimePickerButtonDisabled]: option.disabled,
        })}
        role="button"
        tabIndex={0}
        data-is-selected={selectedTime === option.label}
        onClick={() => handleSelectOption(option, close)}
      >
        <S>{option.label}</S>
      </div>
    ))}
  </div>
);

export const TimePickerButton = ({
  disabled = false,
  value,
  timeZone,
  onChange = () => null,
  unit = TimeIntervals.QUARTER_HOUR,
  times,
  className,
  wrapperClassName,
  buttonClassName,
  hideLabel = false,
  label,
  error,
  fixedLabel = false,
  showTimezone = false,
  testId,
  placeholder,
  ButtonComponent,
  locale = 'en-US',
}: TimePickerButtonProps) => {
  const [dateTimeValue, setDateTimeValue] = useState<DateTime | undefined>(
    value ?? undefined
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [optionsRef] = useScrollToSelectedChild(selectedIndex, true);

  useEffect(() => {
    setDateTimeValue(value ?? undefined);
  }, [value]);

  const options: Option[] = useMemo(() => {
    // get the start of the day for the date time value
    const dayStartDate = dateTimeValue
      ? dateTimeValue.startOf('day')
      : DateTime.fromJSDate(new Date(), {
          zone: timeZone,
        }).startOf('day');

    if (times) {
      return mapTimesToTimeOptions(times, showTimezone, timeZone, locale);
    }

    return getTimesFromUnit(unit, dayStartDate, showTimezone, timeZone, locale);
  }, [times, unit, timeZone, dateTimeValue, showTimezone, locale]);

  useLayoutEffect(() => {
    setSelectedIndex(
      options.findIndex(option => {
        if (!dateTimeValue) {
          return false;
        }

        return isOptionSelectedTime(option, dateTimeValue, timeZone);
      })
    );
  }, [dateTimeValue, timeZone, options]);

  const selectedTime = dateTimeValue
    ? formatTime(dateTimeValue, showTimezone, timeZone, locale)
    : '';

  const handleSelectOption = useCallback(
    (option: Option, close: () => void) => {
      if (option.disabled) {
        return;
      }

      if (onChange) {
        onChange(option.value);
      }

      close();
    },
    [onChange]
  );

  const trigger = ButtonComponent ? (
    (props: Record<string, unknown>) => {
      return ButtonComponent({
        value: selectedTime,
        onOpen: () => {},
        ...props,
      });
    }
  ) : (
    <DefaultButton
      buttonText={selectedTime}
      buttonClassName={buttonClassName}
      disabled={disabled}
      testId={testId}
      hideLabel={hideLabel}
      label={label}
      placeholder={placeholder}
      error={error}
      fixedLabel={fixedLabel}
      styles={styles}
    />
  );

  return (
    <span className={cx(styles.DatePickerButtons, className)}>
      <span className={cx(styles.wrapper, wrapperClassName)}>
        <Popup zIndex={6} trigger={trigger}>
          {({ close }) =>
            renderOptions({
              close,
              options,
              selectedTime,
              handleSelectOption,
              optionsRef,
            })
          }
        </Popup>
      </span>
    </span>
  );
};
